import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Check, Loader2, Sparkles, Plus, XCircle, ChevronDown, ShieldCheck,
  AlertTriangle, StickyNote, Eye, UserCheck, Edit3, ClipboardCheck,
} from "lucide-react";
import api, { formatErr } from "../lib/api";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../lib/bookingStatus";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import NoteTemplatesRow from "./NoteTemplatesRow";
import { deDateLong, deDateMedium, deDateShort, deDateTime } from "../lib/dateFormat";

// Feb 2026 · 20 Jun 2026 · Datei-Split von BookingDetailModal.jsx.
// Diese selbständigen Teil-Komponenten wurden 1:1 (verbatim) aus dem Modal
// ausgelagert, um die Hauptdatei wartbar zu halten. Logik unverändert.
// Stabile Leer-Werte (Parität mit dem Elternmodul).
const EMPTY_LIST = [];
const EMPTY_OBJ = {};

const EXTRA_CATEGORY_LABEL = {
  interior:   "Innenbereich",
  outdoor:    "Außenbereich",
  additional: "Weitere Services",
};

// Standard-service subcategory labels — Inklusiv-Leistungen split
const STD_SUBCAT_LABEL = {
  interior:   "Innenbereich",
  outdoor:    "Außenbereich",
  management: "Verwaltung",
};

const STD_SUBCAT_ORDER = ["interior", "outdoor", "management"];
const EXTRA_CATEGORY_ORDER = ["interior", "outdoor", "additional"];

/** Read-only team preview of the customer's PRIVATE arrival/departure
 *  checklist. These items are personal reminders (Fenster zumachen, Alarm
 *  scharfstellen, Müll rausbringen…) and MUST NOT affect the booking status
 *  — the calendar only flips green when all TEAM services are done. This
 *  panel exists so the concierge has a heads-up view of what the customer
 *  is doing on their side, without any tick controls. */

/**
 * Feb 2026 · 11 Sep 2026 · Header-Zweizeiler mit den beiden Zuständigen.
 * User-Directive: „Direkt unterhalb der Hauptzeile (Art, Objekt, Status)
 * sollen immer zwei Zeilen sichtbar sein: 1. Haupt-Concierge des Objekts,
 * 2. Verantwortlicher für diese Buchung. Beide immer anzeigen, auch wenn
 * identisch."
 *
 * Datenquelle: `booking.assigned_concierge_id` (vom Backend als Property-
 * Fallback enriched, siehe bookings_router.py:796) + `booking.assigned_to`
 * (direkte Buchungs-Zuweisung). Namen kommen live aus /team/members.
 */
function BookingHeaderAssigneeLines({ booking }) {
  const [members, setMembers] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/team/members");
        setMembers((data || []).filter((u) => ["admin", "concierge"].includes(u.role)));
      } catch { /* silent */ }
    })();
  }, []);
  const nameOf = (id) => {
    if (!id) return null;
    const m = members.find((x) => x.id === id);
    return m?.name || m?.email || null;
  };
  const hauptId = booking?.assigned_concierge_id || null;
  const respId = booking?.assigned_to || null;
  const hauptName = nameOf(hauptId);
  const respName = nameOf(respId);
  const bothSameId = hauptId && respId && hauptId === respId;
  return (
    <div className="mt-2 flex flex-col gap-0.5" data-testid="booking-header-assignee-lines">
      <div className="text-[11.5px] leading-tight" data-testid="booking-header-haupt-concierge">
        <span className="tracking-[0.14em] uppercase text-navy-dark/50 mr-1">Haupt-Concierge · </span>
        {hauptName ? (
          <span className="text-navy-dark/85">{hauptName}</span>
        ) : (
          <span className="text-navy-dark/45 italic">— (nicht zugewiesen)</span>
        )}
      </div>
      <div className="text-[11.5px] leading-tight" data-testid="booking-header-responsible">
        <span className="tracking-[0.14em] uppercase text-navy-dark/50 mr-1">Verantwortlich · </span>
        {respName ? (
          <span className="text-navy-dark/85">
            {respName}
            {bothSameId && (
              <span className="ml-1.5 text-[10px] tracking-[0.14em] uppercase text-navy-dark/40"
                    data-testid="booking-header-responsible-same-as-haupt">
                (identisch)
              </span>
            )}
          </span>
        ) : hauptName ? (
          <span className="text-navy-dark/70">
            {hauptName}
            <span className="ml-1.5 text-[10px] tracking-[0.14em] uppercase text-navy-dark/40"
                  data-testid="booking-header-responsible-via-property">
              (via Objekt)
            </span>
          </span>
        ) : (
          <span className="text-navy-dark/45 italic">— (nicht zugewiesen)</span>
        )}
      </div>
    </div>
  );
}

/**
 * Feb 2026 · Verantwortlichen-Picker in der Team-Booking-Detail-Ansicht.
 * Löst die User-Directive „Zeig einen Verantwortlichen-Picker direkt in der
 * Kunden-Buchung, damit der Admin bei fehlendem Objekt-Concierge sofort
 * eingreifen kann". Zeigt:
 *   • Aktueller Assignee (Name + Rolle) oder gold-warnende „Kein
 *     Verantwortlicher"-Zeile — passend zur Grau→Blau-Regel.
 *   • Dropdown mit allen Admins + Concierges aus `/team/members`.
 *   • Speichern via `PATCH /bookings/{id}` mit `assigned_to`.
 * Nur für Team-Modus gerendert (siehe Aufruf-Site).
 */
function BookingAssigneePicker({ booking, pending, setPending, saving }) {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const currentServerAssignee = booking?.assigned_to || "";
  // Feb 2026 · 11 Sep 2026 · Global-Save-Bar-Refactor: der Picker ist eine
  // kontrollierte Komponente. Änderungen fließen in `pending.assigned_to`
  // (Wrapper `{ value }` — so kann `null` von „unset" unterschieden werden).
  // Kein eigenes Save + kein eigener PIN-Dialog mehr.
  const effectiveAssignee = pending ? pending.value : currentServerAssignee;
  const [selected, setSelected] = useState(effectiveAssignee || "");
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    setSelected(effectiveAssignee || "");
  }, [booking?.id, effectiveAssignee]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/team/members");
        setMembers((data || []).filter((m) => ["admin", "concierge"].includes(m.role)));
      } catch (err) {
        console.warn("team/members fetch failed:", err);
      }
    })();
  }, []);

  const currentAssignee = members.find((m) => m.id === effectiveAssignee) || null;
  const currentPropertyConcierge = booking?.assigned_concierge_id || null;
  const propertyConciergeName = members.find((m) => m.id === currentPropertyConcierge)?.name || null;
  const hasAssignee = !!effectiveAssignee;
  const hasAnyResponsibility = !!(effectiveAssignee || currentPropertyConcierge);
  const isDirty = pending !== null && pending?.value !== currentServerAssignee;

  const applyPending = () => {
    if (!selected) {
      toast.error("Bitte einen Verantwortlichen wählen.");
      return;
    }
    if (selected === currentServerAssignee) {
      setPending(null);
    } else {
      setPending({ value: selected });
    }
    setEditing(false);
  };

  // Nur Admins dürfen ändern — Concierges können den aktuellen Status sehen.
  const canEdit = user?.role === "admin";

  return (
    <div
      className="bg-white border border-navy/10 p-4 md:p-5"
      data-testid="booking-modal-assignee-picker"
    >
      <div className="flex items-center gap-2 mb-3">
        <UserCheck className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
        <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>Verantwortlicher</div>
        {isDirty && (
          <span
            className="ml-1 text-[10px] tracking-[0.14em] uppercase px-1.5 py-0.5"
            style={{ background: withAlpha(BRAND_COLORS.gold, 0.15), color: BRAND_COLORS.goldDark }}
            data-testid="booking-modal-assignee-dirty-badge"
          >
            geändert
          </span>
        )}
      </div>

      {!editing && (
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            {hasAssignee ? (
              <div data-testid="booking-modal-assignee-current">
                <div className="text-navy font-medium">
                  {currentAssignee?.name || currentAssignee?.email || "—"}
                </div>
                <div className="text-[11.5px] text-navy-dark/60 mt-0.5">
                  {currentAssignee?.role === "admin" ? "Admin" : "Concierge"}
                  {isDirty
                    ? " · noch nicht gespeichert"
                    : " · direkt der Buchung zugewiesen"}
                </div>
              </div>
            ) : hasAnyResponsibility ? (
              <div data-testid="booking-modal-assignee-property-fallback">
                <div className="text-navy">
                  {propertyConciergeName || "Objekt-Concierge"}
                </div>
                <div className="text-[11.5px] text-navy-dark/60 mt-0.5">
                  Via Objekt-Zuweisung · noch keine Buchungs-Direktzuweisung
                </div>
              </div>
            ) : (
              <div
                className="flex items-start gap-2"
                data-testid="booking-modal-assignee-missing"
              >
                <AlertTriangle
                  className="w-4 h-4 mt-0.5 shrink-0"
                  style={{ color: STATUS_COLORS.red }}
                  strokeWidth={1.8}
                />
                <div>
                  <div className="text-navy font-medium">Kein Verantwortlicher</div>
                  <div className="text-[11.5px] text-navy-dark/60 mt-0.5 max-w-md leading-relaxed">
                    Timeline bleibt <b>grau</b>, bis ein Admin oder Concierge zugewiesen ist
                    (Grau→Blau-Regel).
                  </div>
                </div>
              </div>
            )}
          </div>
          {canEdit && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              disabled={saving}
              data-testid="booking-modal-assignee-edit-btn"
              className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.15em] uppercase text-navy border border-navy/25 hover:border-gold hover:text-gold px-4 py-2 transition-colors shrink-0 disabled:opacity-40"
            >
              <Edit3 className="w-3.5 h-3.5" strokeWidth={1.6} />
              {hasAssignee ? "Ändern" : "Zuweisen"}
            </button>
          )}
        </div>
      )}

      {editing && (
        <div className="flex flex-col md:flex-row gap-2 items-stretch md:items-center">
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            disabled={saving}
            data-testid="booking-modal-assignee-select"
            className="flex-1 bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-navy"
          >
            <option value="">Verantwortlichen wählen …</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name || m.email} · {m.role === "admin" ? "Admin" : "Concierge"}
              </option>
            ))}
          </select>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={applyPending}
              disabled={saving || !selected}
              data-testid="booking-modal-assignee-save-btn"
              className="inline-flex items-center gap-2 text-ivory px-4 py-2 text-[11px] tracking-[0.15em] uppercase hover:opacity-90 disabled:opacity-40"
              style={{ background: BRAND_COLORS.navy }}
              title={`Übernimmt die Änderung in den Batch — gespeichert wird erst mit dem globalen „Alle Änderungen speichern"`}
            >
              <Check className="w-3.5 h-3.5" />
              Übernehmen
            </button>
            <button
              type="button"
              onClick={() => { setEditing(false); setSelected(effectiveAssignee || ""); }}
              disabled={saving}
              data-testid="booking-modal-assignee-cancel-btn"
              className="inline-flex items-center gap-1 text-navy-dark/60 hover:text-navy px-2 py-2 text-[11px] tracking-[0.15em] uppercase"
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function TeamReadOnlyCustomerChecklist({ departure, arrival }) {
  const Section = ({ title, items, testKey }) => {
    if (!items || items.length === 0) return null;
    const done = items.filter((it) => it.done).length;
    return (
      <div className="flex-1 min-w-[220px]" data-testid={`booking-modal-customer-checklist-${testKey}`}>
        <div className="flex items-baseline justify-between mb-1.5">
          <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>{title}</div>
          <div className="label-caps text-navy-dark/60">{done} / {items.length}</div>
        </div>
        <ul className="space-y-1">
          {items.map((it) => (
            <li
              key={it.key}
              data-testid={`booking-modal-customer-check-${testKey}-${it.key}${it.done ? "-done" : ""}`}
              className="flex items-start gap-2 text-[12.5px] leading-snug"
            >
              <span
                className="mt-0.5 w-3.5 h-3.5 border rounded flex items-center justify-center shrink-0"
                style={it.done
                  ? { background: STATUS_COLORS.green, borderColor: STATUS_COLORS.green }
                  : { background: "#fff", borderColor: withAlpha(BRAND_COLORS.navy, 0.30) }}
                aria-hidden
              >
                {it.done && <Check className="w-2 h-2 text-ivory" strokeWidth={3.2} />}
              </span>
              <span className={it.done ? "text-navy-dark/55 line-through decoration-navy-dark/25" : "text-navy-dark"}>
                {it.label}
              </span>
            </li>
          ))}
        </ul>
      </div>
    );
  };
  return (
    <div
      className="border border-navy/10 bg-navy/[0.02] p-4 rounded"
      data-testid="booking-modal-customer-checklist"
    >
      <div className="flex items-center gap-2 mb-3">
        <ClipboardCheck className="w-3.5 h-3.5" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.8} />
        <div>
          <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>Kunden-Checkliste (Ansicht)</div>
          <div className="text-[11px] text-navy-dark/60 italic">
            Persönliche Erledigungen des Kunden — read-only, hat keinen Einfluss auf den Booking-Status.
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-6">
        <Section title="Abreise" items={departure} testKey="departure" />
        <Section title="Anreise" items={arrival} testKey="arrival" />
      </div>
    </div>
  );
}


function DE_LONG(iso) {
  if (!iso) return "";
  try {
    return deDateLong(iso);
  } catch { return iso; }
}

/**
 * Feb 2026 · Concierge-Notiz zur Willkommen-zurück-Mail.
 *
 * User-Directive: „Let the property's concierge add a personal one-liner
 * right before dispatch so returning customers feel it is written for
 * them."
 *
 * - Sichtbar im Team-Modus, ausgenommen stornierte Buchungen.
 * - Concierge sieht + editiert seine eigenen Buchungen (via
 *   assigned_to ODER Objekt-Concierge). Admin sieht alle.
 * - Max 500 Zeichen (Backend hard limit).
 * - Wird vom Cron `auto-customer-messages` am Rückkehr-Tag als goldene
 *   Blockquote in die automatische Willkommen-zurück-Mail eingebettet.
 */
function WelcomeBackNoteEditor({ booking, onSaved }) {
  const [note, setNote] = useState((booking?.welcome_back_note || ""));
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Feb 2026 · Claude-Assistenz — 3 Vorschläge zum Einklicken.
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  useEffect(() => {
    setNote(booking?.welcome_back_note || "");
    setDirty(false);
    setSuggestions([]);
  }, [booking?.id, booking?.welcome_back_note]);
  const MAX = 500;
  const remaining = MAX - (note?.length || 0);
  const onChange = (e) => {
    setNote(e.target.value);
    setDirty(true);
  };
  const save = async () => {
    if (!booking?.id) return;
    setSaving(true);
    try {
      const { data } = await api.patch(
        `/bookings/${booking.id}/welcome-back-note`,
        { note: note.trim() },
      );
      toast.success(
        note.trim()
          ? "Notiz gespeichert · wird am Rückkehr-Tag in die Mail eingefügt."
          : "Notiz entfernt.",
      );
      onSaved?.(data);
      setDirty(false);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };
  const fetchSuggestions = async () => {
    if (!booking?.id) return;
    setSuggesting(true);
    try {
      const { data } = await api.post(
        `/bookings/${booking.id}/welcome-back-note/suggestions`,
      );
      const arr = Array.isArray(data?.suggestions) ? data.suggestions : [];
      if (arr.length === 0) {
        toast.info("Claude hat aktuell keine Vorschläge — bitte selbst formulieren.");
      }
      setSuggestions(arr);
    } catch (e) {
      const status = e?.response?.status;
      const msg =
        status === 503 ? "Claude-Assistenz gerade nicht erreichbar."
        : formatErr(e?.response?.data?.detail) || "Vorschläge konnten nicht geladen werden.";
      toast.error(msg);
    } finally {
      setSuggesting(false);
    }
  };
  const applySuggestion = (text) => {
    const trimmed = String(text || "").slice(0, MAX);
    setNote(trimmed);
    setDirty(true);
    setSuggestions([]);
  };
  const hasNote = !!(booking?.welcome_back_note || "").trim();
  return (
    <div
      className="border border-navy/10 p-4 rounded space-y-2"
      style={{ background: "#FAF7F0" }}
      data-testid="welcome-back-note-editor"
    >
      <div className="flex items-center gap-2">
        <StickyNote className="w-3.5 h-3.5" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.8} />
        <div className="min-w-0 flex-1">
          <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>Concierge-Notiz · Willkommen zurück</div>
          <div className="text-[11px] text-navy-dark/60 leading-snug">
            Ein persönlicher Einzeiler — landet am Rückkehr-Tag als goldene
            Zeile in der automatischen Willkommen-zurück-Mail.
          </div>
        </div>
        {hasNote && (
          <span
            className="text-[10.5px] tracking-[0.14em] uppercase px-2 py-0.5 rounded-full"
            style={{ background: withAlpha(BRAND_COLORS.gold, 0.18), color: BRAND_COLORS.goldDark }}
            data-testid="welcome-back-note-active-badge"
          >
            Aktiv
          </span>
        )}
      </div>
      <textarea
        rows={2}
        value={note}
        onChange={onChange}
        maxLength={MAX}
        placeholder="z. B. „Der Kamin ist eingeheizt und Ihre Lieblingsblumen stehen am Eingang."
        data-testid="welcome-back-note-input"
        className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px] leading-relaxed resize-none"
      />

      {/* Feb 2026 · Claude Sonnet 5 Vorschläge — der Concierge klickt einen
          Vorschlag und übernimmt ihn direkt in die Notiz. */}
      <div className="flex items-center justify-between gap-2 pt-1">
        <button
          type="button"
          onClick={fetchSuggestions}
          disabled={suggesting || !booking?.id}
          data-testid="welcome-back-note-suggest"
          className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.14em] uppercase px-3 py-1.5 border transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            borderColor: withAlpha(BRAND_COLORS.gold, 0.55),
            color: BRAND_COLORS.goldDark,
            background: "transparent",
          }}
          title="Claude Sonnet 5 schlägt 3 persönliche Einzeiler vor"
        >
          {suggesting ? (
            <>
              <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2} />
              Claude denkt …
            </>
          ) : (
            <>
              <Sparkles className="w-3 h-3" strokeWidth={2} />
              Vorschläge holen
            </>
          )}
        </button>
        <span className={`text-[10.5px] tabular-nums ${remaining < 40 ? "text-red-attn" : "text-navy-dark/50"}`}>
          {remaining} Zeichen übrig
        </span>
      </div>

      {suggestions.length > 0 && (
        <div className="space-y-2 pt-1" data-testid="welcome-back-note-suggestions">
          <div className="label-caps text-[10px]" style={{ color: BRAND_COLORS.goldDark }}>
            Claude · 3 Vorschläge
          </div>
          {suggestions.map((s, i) => (
            <button
              key={s}
              type="button"
              onClick={() => applySuggestion(s)}
              data-testid={`welcome-back-note-suggestion-${i}`}
              className="w-full text-left bg-white hover:bg-gold/10 border border-navy/15 hover:border-gold/50 px-3 py-2 text-[12.5px] leading-relaxed text-navy transition-colors"
            >
              „{s}"
            </button>
          ))}
          <button
            type="button"
            onClick={() => setSuggestions([])}
            data-testid="welcome-back-note-suggestions-close"
            className="text-[10.5px] tracking-[0.12em] uppercase text-navy-dark/50 hover:text-navy"
          >
            Vorschläge schließen
          </button>
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        {hasNote && !dirty && (
          <button
            type="button"
            onClick={() => { setNote(""); setDirty(true); }}
            data-testid="welcome-back-note-clear"
            className="text-[11px] tracking-[0.12em] uppercase text-navy-dark/60 hover:text-red-attn"
          >
            Entfernen
          </button>
        )}
        <button
          type="button"
          onClick={save}
          disabled={saving || !dirty}
          data-testid="welcome-back-note-save"
          className="inline-flex items-center gap-1.5 bg-navy text-ivory hover:bg-navy-dark disabled:opacity-50 disabled:cursor-not-allowed text-[11px] tracking-[0.14em] uppercase px-3 py-1.5"
        >
          {saving ? (
            <>
              <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2} />
              Speichere …
            </>
          ) : (
            "Speichern"
          )}
        </button>
      </div>
    </div>
  );
}

function TeamServicesChecklist({ booking, extras, standardServices, pending, setPending, saving }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  // Feb 2026 · User-Directive · Abgewählte Leistungen des Kunden landen
  // NICHT mehr in der Haupt-Checkliste, sondern in einer separaten,
  // standardmäßig eingeklappten Kachel am Ende — nur auf Wunsch ausklappbar.
  const [skippedOpen, setSkippedOpen] = useState(false);

  // Feb 2026 · 11 Sep 2026 · Global-Save-Bar-Refactor: Checkliste ist jetzt
  // eine kontrollierte Komponente. Puffer + Speichern liegen im Modal —
  // hier nur noch Reads/Writes über `pending` + `setPending`.
  const pendingCompleted = pending?.completed;
  const pendingServices = pending?.services;
  const pendingDisabledStd = pending?.disabled_std;
  const serverCompleted = booking.completed_services || EMPTY_LIST;
  const serverServices = booking.services || EMPTY_LIST;
  const serverDisabled = booking.disabled_standard_services || EMPTY_LIST;

  const disabledSet = useMemo(
    () => new Set(pendingDisabledStd ?? serverDisabled),
    [pendingDisabledStd, serverDisabled],
  );
  const servicesSet = useMemo(
    () => new Set(pendingServices ?? serverServices),
    [pendingServices, serverServices],
  );
  const completedSet = useMemo(
    () => new Set(pendingCompleted ?? serverCompleted),
    [pendingCompleted, serverCompleted],
  );
  const serviceNotes = booking.service_notes || EMPTY_OBJ;

  // Keine lokale Save-Bar mehr — Anzeige liegt jetzt global im Modal-Footer.

  // Enabled inclusive (Inklusiv) — only what the customer LEFT ticked. If the
  // customer opted out via disabled_standard_services, we skip that item.
  const stdEnabled = useMemo(
    () => (standardServices || []).filter((s) => !disabledSet.has(s.key)),
    [standardServices, disabledSet],
  );
  // Selected extras (Zusatz) — extras the customer explicitly ticked.
  const extrasSelected = useMemo(
    () => (extras || []).filter((e) => servicesSet.has(e.id)),
    [extras, servicesSet],
  );
  // Feb 2026 · Standard-Services, die der Kunde für diesen Termin
  // abgewählt hat. Werden unten in einer eingeklappten Kachel gezeigt —
  // Admin/Concierge sieht sofort, was der Kunde bewusst gestrichen hat,
  // ohne dass die Haupt-Checkliste damit zugemüllt wird.
  const stdSkipped = useMemo(
    () => (standardServices || []).filter((s) => disabledSet.has(s.key)),
    [standardServices, disabledSet],
  );
  // Portfolio items NOT yet on this booking — feed the "Service hinzufügen" picker.
  const stdAvailable = useMemo(
    () => (standardServices || []).filter((s) => disabledSet.has(s.key)),
    [standardServices, disabledSet],
  );
  const extrasAvailable = useMemo(
    () => (extras || []).filter((e) => !servicesSet.has(e.id)),
    [extras, servicesSet],
  );

  const allItems = useMemo(
    () => [
      ...stdEnabled.map((s) => ({ kind: "std", id: s.key, label: s.name_de || s.name_en, note: serviceNotes[s.key] })),
      ...extrasSelected.map((e) => ({ kind: "extra", id: e.id, label: e.name_de || e.name_en, note: serviceNotes[e.id] })),
    ],
    [stdEnabled, extrasSelected, serviceNotes],
  );
  const doneCount = allItems.filter((i) => completedSet.has(i.id)).length;
  const totalCount = allItems.length;
  const allDone = totalCount > 0 && doneCount === totalCount;

  const toggleDone = (key) => {
    if (saving) return;
    setPending((prev) => {
      const cur = new Set(prev.completed ?? serverCompleted);
      if (cur.has(key)) cur.delete(key);
      else cur.add(key);
      return { ...prev, completed: Array.from(cur).sort() };
    });
  };

  const addService = (kind, id) => {
    if (saving) return;
    if (kind === "std") {
      setPending((prev) => {
        const cur = new Set(prev.disabled_std ?? serverDisabled);
        cur.delete(id);
        return { ...prev, disabled_std: Array.from(cur).sort() };
      });
    } else {
      setPending((prev) => {
        const cur = new Set(prev.services ?? serverServices);
        cur.add(id);
        return { ...prev, services: Array.from(cur).sort() };
      });
    }
    setPickerOpen(false);
  };

  return (
    <div data-testid="team-services-checklist">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4 text-navy" strokeWidth={1.6} />
          <div className="label-caps">Aufgaben für diesen Termin</div>
        </div>
        <div
          className={`text-[11.5px] font-medium tracking-wide ${allDone ? "text-[#4B7A55]" : "text-navy-dark/60"}`}
          data-testid="team-checklist-progress"
        >
          {allDone ? (
            <span className="inline-flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5" strokeWidth={3} />
              Alle {totalCount} erledigt
            </span>
          ) : (
            <>{doneCount} / {totalCount} erledigt</>
          )}
        </div>
      </div>

      {totalCount === 0 ? (
        <div
          className="bg-white border border-dashed border-navy/20 p-4 text-center text-[13px] text-navy-dark/60"
          data-testid="team-checklist-empty"
        >
          Der Kunde hat für diesen Termin keine Inklusiv- oder Zusatzleistungen angehakt.
          <br />
          <span className="text-[11.5px]">Über „Service hinzufügen" können Sie manuell ergänzen.</span>
        </div>
      ) : (
        <ul className="space-y-1.5">
          {allItems.map((item) => {
            const isDone = completedSet.has(item.id);
            const isBusy = saving;
            const isExtra = item.kind === "extra";
            return (
              <li key={`${item.kind}-${item.id}`}>
                <button
                  type="button"
                  onClick={() => toggleDone(item.id)}
                  disabled={isBusy}
                  data-testid={`team-checklist-item-${item.id}${isDone ? "-done" : "-open"}`}
                  className={`w-full text-left flex items-start gap-3 p-3 bg-white border transition-colors ${
                    isExtra
                      ? "border-gold/50 hover:border-gold"
                      : "border-navy/15 hover:border-navy/40"
                  } ${isBusy ? "cursor-wait" : ""}`}
                >
                  <div
                    className={`w-5 h-5 border-2 flex items-center justify-center shrink-0 mt-0.5 transition-colors ${
                      isDone
                        ? isExtra
                          ? "border-transparent"
                          : "border-navy bg-navy"
                        : isExtra
                          ? "border-gold/60"
                          : "border-navy-dark/40"
                    }`}
                    style={isDone && isExtra ? { background: BRAND_COLORS.gold } : {}}
                  >
                    {isBusy ? (
                      <Loader2 className="w-3.5 h-3.5 text-navy-dark/60 animate-spin" strokeWidth={2} />
                    ) : isDone ? (
                      <Check className="w-3.5 h-3.5 text-ivory" strokeWidth={3} />
                    ) : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[13px] ${isDone ? "line-through text-navy-dark/70" : "text-navy"}`}>
                        {item.label}
                      </span>
                      {isExtra ? (
                        <span className="inline-flex items-center gap-1 text-[9.5px] tracking-[0.14em] uppercase font-medium" style={{ color: BRAND_COLORS.gold }}>
                          <Sparkles className="w-2.5 h-2.5" strokeWidth={2} />
                          Zusatz
                        </span>
                      ) : (
                        <span className="text-[9.5px] tracking-[0.14em] uppercase text-navy-dark/45 font-medium">
                          Inklusiv
                        </span>
                      )}
                    </div>
                    {item.note && (
                      <div className="mt-1 pl-2 border-l-2 border-gold/50 text-[11.5px] italic text-navy-dark/70 leading-snug">
                        „{item.note}"
                      </div>
                    )}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* „Service hinzufügen" — team fallback to add missing items */}
      <div className="mt-4 relative">
        <button
          type="button"
          onClick={() => setPickerOpen((o) => !o)}
          data-testid="team-checklist-add-btn"
          className={`w-full inline-flex items-center justify-center gap-2 border border-dashed px-4 py-2.5 text-[11px] tracking-[0.18em] uppercase transition-colors ${
            pickerOpen
              ? "border-navy bg-navy text-ivory"
              : "border-navy/40 hover:border-navy hover:bg-navy hover:text-ivory text-navy-dark/70"
          }`}
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={1.8} />
          Service hinzufügen
        </button>
        {pickerOpen && (
          <div
            className="mt-2 border border-navy/15 bg-white shadow-lg max-h-72 overflow-y-auto"
            data-testid="team-checklist-picker"
          >
            {stdAvailable.length === 0 && extrasAvailable.length === 0 ? (
              <div className="p-4 text-center text-[12.5px] text-navy-dark/55">
                Alle Portfolio-Services sind bereits Teil dieser Buchung.
              </div>
            ) : (
              <>
                {stdAvailable.length > 0 && (
                  <div>
                    <div className="px-3 py-2 text-[10px] tracking-widest uppercase text-navy-dark/60 bg-navy/5 border-b border-navy/5">
                      Inklusiv-Leistungen
                    </div>
                    <ul>
                      {stdAvailable.map((s) => (
                        <li key={s.key}>
                          <button
                            type="button"
                            onClick={() => addService("std", s.key)}
                            disabled={saving}
                            data-testid={`team-checklist-add-std-${s.key}`}
                            className="w-full text-left px-3 py-2 text-[13px] text-navy hover:bg-navy/5 border-b border-navy/5 last:border-b-0 flex items-center gap-2"
                          >
                            <Plus className="w-3 h-3 shrink-0 text-navy-dark/50" strokeWidth={2} />
                            {s.name_de || s.name_en}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {extrasAvailable.length > 0 && (
                  <div>
                    <div
                      className="px-3 py-2 text-[10px] tracking-widest uppercase font-medium border-b border-navy/5"
                      style={{ color: BRAND_COLORS.goldDark, background: withAlpha(BRAND_COLORS.gold, 0.10) }}
                    >
                      Zusatzleistungen
                    </div>
                    <ul>
                      {extrasAvailable.map((e) => (
                        <li key={e.id}>
                          <button
                            type="button"
                            onClick={() => addService("extra", e.id)}
                            disabled={saving}
                            data-testid={`team-checklist-add-extra-${e.key || e.id}`}
                            className="w-full text-left px-3 py-2 text-[13px] text-navy hover:bg-gold/10 border-b border-navy/5 last:border-b-0 flex items-center gap-2"
                          >
                            <Sparkles className="w-3 h-3 shrink-0" style={{ color: BRAND_COLORS.gold }} strokeWidth={2} />
                            {e.name_de || e.name_en}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Feb 2026 · User-Directive · Abgewählte Leistungen — separate,
          standardmäßig eingeklappte Kachel am Ende. Der Admin/Concierge
          sieht auf einen Blick, was der Kunde für diesen Termin bewusst
          gestrichen hat, ohne dass die Haupt-Checkliste damit zugemüllt
          wird. Klick auf die Kopfzeile klappt die Liste aus; ein
          „Wieder aktivieren"-Button pro Item schiebt es zurück in die
          aktive Checkliste. */}
      {stdSkipped.length > 0 && (
        <div className="mt-4" data-testid="team-checklist-skipped">
          <button
            type="button"
            onClick={() => setSkippedOpen((o) => !o)}
            data-testid="team-checklist-skipped-toggle"
            aria-expanded={skippedOpen ? "true" : "false"}
            className="w-full flex items-center justify-between gap-3 px-4 py-3 border border-navy/10 bg-navy/[0.02] hover:bg-navy/5 transition-colors text-left"
          >
            <div className="flex items-center gap-2">
              <XCircle className="w-4 h-4" style={{ color: STATUS_COLORS.red }} strokeWidth={1.6} />
              <div>
                <div className="label-caps text-navy-dark/75">
                  Abgewählte Leistungen
                </div>
                <div className="text-[11.5px] text-navy-dark/55 mt-0.5">
                  {stdSkipped.length} Position{stdSkipped.length === 1 ? "" : "en"} · vom Kunden für diesen Termin gestrichen
                </div>
              </div>
            </div>
            <ChevronDown
              className="w-4 h-4 text-navy-dark/50 transition-transform shrink-0"
              style={{ transform: skippedOpen ? "rotate(180deg)" : "rotate(0deg)" }}
              strokeWidth={1.6}
            />
          </button>
          {skippedOpen && (
            <ul className="mt-2 space-y-1.5" data-testid="team-checklist-skipped-list">
              {stdSkipped.map((s) => {
                const note = serviceNotes[s.key];
                const isBusy = saving;
                return (
                  <li
                    key={s.key}
                    className="flex items-start justify-between gap-3 p-3 bg-white border border-navy/10"
                    data-testid={`team-checklist-skipped-item-${s.key}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] leading-snug line-through decoration-navy-dark/30 text-navy-dark/70">
                        {s.name_de || s.name_en}
                      </div>
                      {note && (
                        <div className="mt-1 pl-2 border-l-2 border-navy-dark/20 text-[11.5px] italic text-navy-dark/60 leading-snug">
                          „{note}"
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => addService("std", s.key)}
                      disabled={isBusy}
                      data-testid={`team-checklist-skipped-reactivate-${s.key}`}
                      className="inline-flex items-center gap-1 shrink-0 text-[10.5px] tracking-[0.14em] uppercase text-navy hover:text-gold-dark border border-navy/25 hover:border-gold px-2.5 py-1 disabled:opacity-40 transition-colors"
                      title="In die Checkliste zurückholen"
                    >
                      {isBusy ? <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2} /> : <Plus className="w-3 h-3" strokeWidth={2} />}
                      Wieder aktivieren
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function CancelConfirmation({ onKeep, onConfirm, busy }) {
  const { lang } = useI18n();
  const de = lang !== "en";
  return (
    <div className="space-y-3" data-testid="booking-detail-cancel-confirm">
      <div className="flex items-start gap-3">
        <XCircle className="w-5 h-5 mt-0.5 shrink-0" style={{ color: STATUS_COLORS.red }} strokeWidth={1.5} />
        <div>
          <div className="font-display text-lg text-navy leading-tight">
            {de ? "Möchten Sie diesen Termin wirklich stornieren?" : "Do you really want to cancel this booking?"}
          </div>
          <div className="text-[13px] text-navy-dark/60 mt-1 leading-relaxed">
            {de
              ? "Die Abwesenheit wird aus Ihrem Kalender entfernt und Ihr Concierge sofort informiert. Diese Aktion kann nicht rückgängig gemacht werden."
              : "The absence will be removed from your calendar and your concierge notified immediately. This action cannot be undone."}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onKeep}
          disabled={busy}
          data-testid="booking-detail-cancel-keep"
          className="text-[11px] tracking-widest uppercase text-navy-dark/70 hover:text-navy px-3 py-2"
        >
          {de ? "Doch behalten" : "Keep it"}
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={busy}
          data-testid="booking-detail-cancel-confirm-btn"
          className="inline-flex items-center gap-2 text-ivory px-5 py-2.5 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 disabled:opacity-60"
          style={{ background: STATUS_COLORS.red }}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          {de ? "Ja, stornieren" : "Yes, cancel"}
        </button>
      </div>
    </div>
  );
}

function BookingEditForm({ booking, extras, standardServices, onCancel, onCommit, saving, externalOverlap = null, onEditingDates }) {
  const { lang } = useI18n();
  const de = lang !== "en";
  const [fromDate, setFromDate] = useState(booking.from_date || "");
  const [toDate, setToDate] = useState(booking.to_date || "");
  const [notes, setNotes] = useState(booking.notes || "");
  const [services, setServices] = useState(booking.services || []);
  const [disabledStd, setDisabledStd] = useState(
    new Set(booking.disabled_standard_services || [])
  );
  const [serviceNotes, setServiceNotes] = useState(booking.service_notes || {});
  const [openNoteKeys, setOpenNoteKeys] = useState(() => {
    // Auto-open note editors for services that already have a saved note.
    return new Set(Object.entries(booking.service_notes || {})
      .filter(([, v]) => (v || "").trim())
      .map(([k]) => k));
  });
  const [inclusiveOpen, setInclusiveOpen] = useState(false); // COLLAPSED by default (per Anforderung)
  const busy = saving;
  // Inline overlap banner — populated when backend returns HTTP 409 because
  // the new date range would collide with another absence. Feb 2026:
  // wenn ein externer Overlap-Kontext von der Modal-Ebene (Global-Save-Bar)
  // durchgereicht wird, dominiert dieser die lokale Anzeige — beide teilen
  // sich das rote Feedback (Rand + Konflikt-Zeile).
  const [overlapError, setOverlapError] = useState(null);
  useEffect(() => { setOverlapError(null); }, [fromDate, toDate]);
  const effectiveOverlap = overlapError || externalOverlap || null;

  // Group extras by category (interior / outdoor / additional)
  const extrasByCategory = useMemo(() => {
    const buckets = { interior: [], outdoor: [], additional: [] };
    for (const ex of extras || []) {
      const cat = (ex.category || "additional").toLowerCase();
      (buckets[cat] || buckets.additional).push(ex);
    }
    return buckets;
  }, [extras]);

  // Group standard services by subcategory (interior / outdoor / management)
  const stdBySubcategory = useMemo(() => {
    const buckets = { interior: [], outdoor: [], management: [] };
    for (const s of standardServices || []) {
      const sub = (s.subcategory || "management").toLowerCase();
      (buckets[sub] || buckets.management).push(s);
    }
    return buckets;
  }, [standardServices]);

  const selectedExtrasCount = useMemo(
    () => (extras || []).filter((e) => services.includes(e.id)).length,
    [services, extras]
  );

  const stdTotal = (standardServices || []).length;
  const stdEnabledCount = stdTotal - disabledStd.size;
  const inclusiveNotesCount = useMemo(
    () => (standardServices || []).filter((s) => (serviceNotes[s.key] || "").trim()).length,
    [standardServices, serviceNotes]
  );

  // --- Toggling helpers ---
  const toggleExtra = (id) => setServices((cur) =>
    cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]
  );
  const toggleStdEnabled = (key) => setDisabledStd((cur) => {
    const next = new Set(cur);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });
  const toggleNoteEditor = (k) => setOpenNoteKeys((cur) => {
    const next = new Set(cur);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    return next;
  });
  const setNoteFor = (k, val) => setServiceNotes((cur) => ({ ...cur, [k]: val }));

  const save = () => {
    if (!fromDate || !toDate) {
      toast.error(de ? "Bitte Abreise- und Anreise-Datum ausfüllen." : "Please enter both departure and return dates.");
      return;
    }
    if (fromDate > toDate) {
      toast.error(de ? "Abreise-Datum liegt nach dem Anreise-Datum." : "Departure date is after return date.");
      return;
    }
    // Feb 2026 · 11 Sep 2026 · Global-Save-Bar-Refactor: kein direkter
    // PATCH mehr, keine PIN-Abfrage in der Untermaske. Nur „Übernehmen"
    // in den globalen Puffer schieben; das Modal-Level entscheidet
    // dann, wann alles gemeinsam mit einem PIN gespeichert wird.
    const cleanNotes = Object.fromEntries(
      Object.entries(serviceNotes).filter(([, v]) => (v || "").trim())
    );
    onCommit?.({
      from_date: fromDate,
      to_date: toDate,
      services,
      disabled_standard_services: Array.from(disabledStd),
      service_notes: cleanNotes,
      notes,
    });
  };

  return (
    <div className="space-y-5" data-testid="booking-edit-form">
      {/* Zeitraum */}
      <div>
        <div className="label-caps mb-2" style={{ color: BRAND_COLORS.gold }}>{de ? "Zeitraum ändern" : "Change period"}</div>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-[11px] tracking-widest uppercase text-navy-dark/60">
            {de ? "Abreise" : "Departure"}
            <input
              type="date"
              value={fromDate}
              onChange={(e) => { setFromDate(e.target.value); onEditingDates?.(); }}
              disabled={busy}
              data-testid="booking-edit-from-date"
              data-overlap={effectiveOverlap ? "true" : undefined}
              className="mt-1 w-full bg-white focus:outline-none px-3 py-2 text-[13px] text-navy-dark border transition-colors"
              style={effectiveOverlap
                ? { borderColor: STATUS_COLORS.red, boxShadow: `0 0 0 1px ${withAlpha(STATUS_COLORS.red, 0.35)}` }
                : undefined}
            />
          </label>
          <label className="text-[11px] tracking-widest uppercase text-navy-dark/60">
            {de ? "Anreise" : "Return"}
            <input
              type="date"
              value={toDate}
              onChange={(e) => { setToDate(e.target.value); onEditingDates?.(); }}
              disabled={busy}
              data-testid="booking-edit-to-date"
              data-overlap={effectiveOverlap ? "true" : undefined}
              className="mt-1 w-full bg-white focus:outline-none px-3 py-2 text-[13px] text-navy-dark border transition-colors"
              style={effectiveOverlap
                ? { borderColor: STATUS_COLORS.red, boxShadow: `0 0 0 1px ${withAlpha(STATUS_COLORS.red, 0.35)}` }
                : undefined}
            />
          </label>
        </div>
        {effectiveOverlap && (
          <div
            className="mt-3 flex items-start gap-2 border p-3 rounded"
            style={{ background: withAlpha(STATUS_COLORS.red, 0.06), borderColor: STATUS_COLORS.red }}
            data-testid="booking-edit-overlap-warning"
            role="alert"
          >
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.8} style={{ color: STATUS_COLORS.red }} />
            <div className="flex-1 min-w-0">
              <div className="text-[12.5px] font-medium leading-snug" style={{ color: STATUS_COLORS.red }}>
                {de
                  ? "Sie haben für diesen Zeitraum bereits eine Abwesenheit geplant. Bitte ändern Sie den bestehenden Termin."
                  : "You already have an absence planned for this period. Please adjust the existing entry."}
              </div>
              {effectiveOverlap.from && effectiveOverlap.to && (
                <div
                  className="mt-1 text-[11.5px] leading-snug"
                  style={{ color: withAlpha(STATUS_COLORS.red, 0.85) }}
                  data-testid="booking-edit-overlap-details"
                >
                  {de ? "Konflikt mit: " : "Conflicts with: "}{deDateMedium(effectiveOverlap.from)}
                  {" → "}
                  {deDateMedium(effectiveOverlap.to)}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ===== INKLUSIV-LEISTUNGEN (oben, standardmäßig eingeklappt) ===== */}
      {stdTotal > 0 && (
        <div
          className="border border-navy/10 bg-white/60"
          data-testid="booking-edit-inclusive-section"
        >
          <button
            type="button"
            onClick={() => setInclusiveOpen((v) => !v)}
            data-testid="booking-edit-inclusive-toggle"
            className="w-full flex items-center justify-between text-left px-3 py-3"
            aria-expanded={inclusiveOpen}
          >
            <span className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" style={{ color: STATUS_COLORS.green }} strokeWidth={1.6} />
              <span className="text-[11px] tracking-[0.18em] uppercase text-navy-dark/85">
                {de ? "Inklusiv-Leistungen" : "Included services"}
              </span>
              <span
                className="text-[10.5px] tracking-widest uppercase text-navy-dark/50"
                data-testid="booking-edit-inclusive-count"
              >
                ({stdEnabledCount} {de ? "von" : "of"} {stdTotal} {de ? "aktiv" : "active"}{inclusiveNotesCount > 0 ? ` · ${inclusiveNotesCount} ${de ? "mit Notiz" : "with note"}` : ""})
              </span>
            </span>
            <ChevronDown
              className={`w-4 h-4 text-navy-dark/50 transition-transform ${inclusiveOpen ? "rotate-180" : ""}`}
              strokeWidth={1.6}
            />
          </button>
          {!inclusiveOpen && (
            <div className="px-3 pb-3 -mt-1 text-[11.5px] text-navy-dark/55 italic leading-relaxed">
              {de
                ? "Diese Leistungen sind Teil Ihrer Mitgliedschaft. Haken entfernen, wenn eine Leistung bei diesem Termin nicht ausgeführt werden soll. Notizfeld für individuelle Wünsche."
                : "These services are part of your membership. Untick when a service should not be performed this time. Note field for individual wishes."}
            </div>
          )}
          {inclusiveOpen && (
            <div className="px-3 pb-3 pt-1 space-y-4">
              {STD_SUBCAT_ORDER.map((sub) => {
                const list = stdBySubcategory[sub];
                if (!list || list.length === 0) return null;
                const enabled = list.filter((s) => !disabledStd.has(s.key)).length;
                return (
                  <div key={sub} data-testid={`booking-edit-inclusive-group-${sub}`}>
                    <div className="flex items-baseline justify-between mb-1.5">
                      <div className="text-[11px] tracking-[0.18em] uppercase text-navy-dark/70">
                        {(de ? STD_SUBCAT_LABEL : { interior: "Interior", outdoor: "Outdoor", management: "Management" })[sub]}
                      </div>
                      <div className="text-[10.5px] tracking-widest uppercase text-navy-dark/45">
                        {enabled} / {list.length}
                      </div>
                    </div>
                    <ul className="space-y-1">
                      {list.map((s) => (
                        <ServiceRow
                          key={s.key}
                          testKey={`inclusive-${s.key}`}
                          label={de ? (s.name_de || s.name_en) : (s.name_en || s.name_de)}
                          checked={!disabledStd.has(s.key)}
                          onToggle={() => toggleStdEnabled(s.key)}
                          noteOpen={openNoteKeys.has(s.key)}
                          onToggleNote={() => toggleNoteEditor(s.key)}
                          note={serviceNotes[s.key] || ""}
                          onNoteChange={(v) => setNoteFor(s.key, v)}
                          disabled={busy}
                        />
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ===== ZUSATZLEISTUNGEN (darunter) ===== */}
      <div data-testid="booking-edit-extras-section">
        <div className="flex items-baseline justify-between mb-3">
          <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>{de ? "Zusatzleistungen" : "Add-on services"}</div>
          <div className="label-caps text-navy-dark/60" data-testid="booking-edit-extras-count">
            {selectedExtrasCount} {de ? "gewählt" : "selected"}
          </div>
        </div>
        <div className="space-y-4">
          {EXTRA_CATEGORY_ORDER.map((cat) => {
            const list = extrasByCategory[cat];
            if (!list || list.length === 0) return null;
            const catSelected = list.filter((e) => services.includes(e.id)).length;
            const CAT_EN = { interior: "Interior", outdoor: "Outdoor", additional: "Additional" };
            return (
              <div key={cat} data-testid={`booking-edit-extras-group-${cat}`}>
                <div className="flex items-baseline justify-between mb-1.5">
                  <div className="text-[11px] tracking-[0.18em] uppercase text-navy-dark/70">
                    {de ? EXTRA_CATEGORY_LABEL[cat] : CAT_EN[cat]}
                  </div>
                  <div className="text-[10.5px] tracking-widest uppercase text-navy-dark/45">
                    {catSelected} / {list.length}
                  </div>
                </div>
                <ul className="space-y-1">
                  {list.map((ex) => (
                    <ServiceRow
                      key={ex.id}
                      testKey={`extra-${ex.key}`}
                      label={de ? (ex.name_de || ex.name_en) : (ex.name_en || ex.name_de)}
                      checked={services.includes(ex.id)}
                      onToggle={() => toggleExtra(ex.id)}
                      noteOpen={openNoteKeys.has(ex.id)}
                      onToggleNote={() => toggleNoteEditor(ex.id)}
                      note={serviceNotes[ex.id] || ""}
                      onNoteChange={(v) => setNoteFor(ex.id, v)}
                      disabled={busy}
                    />
                  ))}
                </ul>
              </div>
            );
          })}
          {(!extras || extras.length === 0) && (
            <div className="text-[12.5px] text-navy-dark/50 italic">Keine Zusatzleistungen im Katalog.</div>
          )}
        </div>
      </div>

      {/* Notizen (allgemein) */}
      <div>
        <label className="label-caps mb-2 block" style={{ color: BRAND_COLORS.gold }}>{de ? "Allgemeine Notiz" : "General note"}</label>
        <textarea
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={busy}
          placeholder={de ? "Optionale Hinweise für Ihren Concierge" : "Optional notes for your concierge"}
          data-testid="booking-edit-notes"
          className="w-full bg-ivory border border-navy/15 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-navy-dark leading-relaxed"
        />
      </div>

      <div className="flex items-center justify-end gap-2 pt-3 border-t border-navy/10">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          data-testid="booking-edit-cancel"
          className="text-[11px] tracking-widest uppercase text-navy-dark/70 hover:text-navy px-3 py-2"
        >
          {de ? "Abbrechen" : "Cancel"}
        </button>
        <button
          type="button"
          onClick={save}
          disabled={busy}
          data-testid="booking-edit-save-btn"
          className="inline-flex items-center gap-2 text-ivory px-5 py-2.5 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 disabled:opacity-60"
          style={{ background: BRAND_COLORS.navy }}
          title={`Übernimmt die Änderungen in den Batch — gespeichert wird erst mit dem globalen „Alle Änderungen speichern"`}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          Übernehmen
        </button>
      </div>
    </div>
  );
}

/** Single row: checkbox + label + note button + inline note editor when open.
 *  Used for BOTH inclusive standard services AND paid extras — identical UX. */
function ServiceRow({ testKey, label, checked, onToggle, noteOpen, onToggleNote, note, onNoteChange, disabled }) {
  const { lang } = useI18n();
  const de = lang !== "en";
  const hasNote = (note || "").trim().length > 0;
  return (
    <li>
      <div className="flex items-start gap-1.5">
        <button
          type="button"
          onClick={onToggle}
          disabled={disabled}
          aria-pressed={checked}
          data-testid={`booking-edit-${testKey}${checked ? "-on" : "-off"}`}
          className="flex-1 min-w-0 flex items-start gap-2 text-[13px] leading-snug text-left px-2 py-1.5 -mx-2 rounded hover:bg-ivory/60 transition-colors"
        >
          <span
            className="mt-0.5 w-4 h-4 border rounded flex items-center justify-center shrink-0"
            style={checked
              ? { background: STATUS_COLORS.green, borderColor: STATUS_COLORS.green }
              : { background: "#fff", borderColor: withAlpha(BRAND_COLORS.navy, 0.30) }}
          >
            {checked && <Check className="w-2.5 h-2.5" style={{ color: BRAND_COLORS.ivory }} strokeWidth={3} />}
          </span>
          <span className={checked ? "text-navy-dark" : "text-navy-dark/50 line-through decoration-navy-dark/25"}>
            {label}
          </span>
        </button>
        <button
          type="button"
          onClick={onToggleNote}
          disabled={disabled}
          aria-pressed={noteOpen}
          aria-label={de ? "Notiz hinzufügen" : "Add note"}
          data-testid={`booking-edit-note-toggle-${testKey}`}
          className={`shrink-0 w-7 h-7 flex items-center justify-center border rounded transition-colors ${
            hasNote
              ? "border-gold bg-gold/15 text-gold hover:bg-gold/25"
              : "border-navy/20 text-navy-dark/50 hover:border-navy/50 hover:text-navy"
          }`}
          title={hasNote ? (de ? "Notiz bearbeiten" : "Edit note") : (de ? "Notiz hinzufügen" : "Add note")}
        >
          <StickyNote className="w-3.5 h-3.5" strokeWidth={1.6} />
        </button>
      </div>
      {noteOpen && (
        <div className="mt-1 ml-6" data-testid={`booking-edit-note-editor-${testKey}`}>
          <NoteTemplatesRow onPick={onNoteChange} testPrefix={testKey} />
          <textarea
            rows={2}
            value={note}
            onChange={(e) => onNoteChange(e.target.value)}
            disabled={disabled}
            placeholder={de ? "Ihre Wünsche zu dieser Leistung …" : "Your wishes for this service …"}
            className="w-full bg-white border border-gold/40 focus:border-gold focus:outline-none px-2.5 py-1.5 text-[12.5px] text-navy-dark leading-snug"
          />
        </div>
      )}
    </li>
  );
}

// „gesehen vom Team"-Sync-Anzeige: humaner „vor 5 Minuten / vor 2 Std"-Text.
function formatSeenRelative(iso, de = true) {
  if (!iso) return null;
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diffMin = Math.max(0, Math.round((now - then) / 60000));
    if (diffMin < 1) return de ? "gerade eben" : "just now";
    if (diffMin < 60) return de ? `vor ${diffMin} Min.` : `${diffMin} min ago`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) return de ? `vor ${diffHr} Std.` : `${diffHr} h ago`;
    const diffDay = Math.round(diffHr / 24);
    if (diffDay < 30) return de ? `vor ${diffDay} Tag${diffDay === 1 ? "" : "en"}` : `${diffDay} day${diffDay === 1 ? "" : "s"} ago`;
    return deDateShort(iso);
  } catch { return null; }
}

function ChecklistSection({ icon: Icon, title, items, kind, bookingId, seenByTeamAt, seenByTeamName, onLocalUpdate, onChanged, testid, readOnly = false, de = true }) {
  const done = items.filter((it) => it.done).length;
  const total = items.length;
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);
  const [busyKey, setBusyKey] = useState(null);
  const seenRel = formatSeenRelative(seenByTeamAt, de);

  const toggle = async (it) => {
    if (busyKey || readOnly) return;
    const nextDone = !it.done;
    // Optimistic local update so the tap feels instant
    const optimistic = items.map((x) => x.key === it.key ? { ...x, done: nextDone } : x);
    onLocalUpdate?.(optimistic);
    setBusyKey(it.key);
    try {
      await api.patch(`/bookings/${bookingId}/checklist-item`, { kind, key: it.key, done: nextDone });
      onChanged?.();
    } catch (err) {
      // Roll back
      onLocalUpdate?.(items);
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div data-testid={testid} data-readonly={readOnly ? "true" : "false"}>
      <div className="flex items-baseline justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
          <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>{title}</div>
        </div>
        <div className="label-caps text-navy-dark/60">{done} / {total}</div>
      </div>
      <div className="h-1 w-full rounded-full overflow-hidden mb-3" style={{ background: withAlpha(BRAND_COLORS.navy, 0.08) }}>
        <div
          className="h-full transition-all"
          style={{ width: `${pct}%`, background: done === total && total > 0 ? STATUS_COLORS.green : BRAND_COLORS.gold }}
        />
      </div>
      <ul className="space-y-1.5">
        {items.map((it) => (
          <li key={it.key}>
            <button
              type="button"
              onClick={() => toggle(it)}
              disabled={busyKey === it.key || readOnly}
              aria-pressed={!!it.done}
              aria-disabled={readOnly ? "true" : undefined}
              data-testid={`checklist-item-${kind}-${it.key}${it.done ? "-done" : ""}`}
              className={`w-full flex items-start gap-2 text-[13px] leading-snug text-left px-2 py-1.5 -mx-2 rounded transition-colors disabled:opacity-60 ${readOnly ? "cursor-not-allowed" : "hover:bg-ivory/60"}`}
            >
              <span
                className="mt-0.5 w-4 h-4 border rounded flex items-center justify-center shrink-0"
                style={it.done
                  ? { background: STATUS_COLORS.green, borderColor: STATUS_COLORS.green }
                  : { background: "#fff", borderColor: withAlpha(BRAND_COLORS.navy, 0.30) }}
                aria-hidden
              >
                {it.done && <Check className="w-2.5 h-2.5" style={{ color: BRAND_COLORS.ivory }} strokeWidth={3} />}
              </span>
              <span className={`flex-1 min-w-0 ${it.done ? "text-navy-dark/55 line-through decoration-navy-dark/25" : "text-navy-dark"}`}>
                {it.label}
              </span>
              {/* „gesehen vom Team"-Sync-Marker: erscheint direkt hinter
                  jedem privaten Punkt, sobald mindestens ein Team-Mitglied
                  die Buchung im Modal geöffnet hat. Keine Klick-Wirkung —
                  reine Sync-Anzeige. */}
              {seenRel && (
                <span
                  className="shrink-0 inline-flex items-center gap-1 text-[9.5px] tracking-[0.1em] uppercase whitespace-nowrap px-1.5 py-0.5 rounded-full ml-2"
                  style={{
                    color: BRAND_COLORS.greenDeep,
                    background: withAlpha(STATUS_COLORS.green, 0.10),
                    border: `1px solid ${withAlpha(STATUS_COLORS.green, 0.30)}`,
                  }}
                  data-testid={`checklist-item-${kind}-${it.key}-seen-by-team`}
                  title={de
                    ? (seenByTeamName
                        ? `Zuletzt eingesehen von ${seenByTeamName} — ${deDateTime(seenByTeamAt)}`
                        : `Zuletzt eingesehen — ${deDateTime(seenByTeamAt)}`)
                    : (seenByTeamName
                        ? `Last viewed by ${seenByTeamName} — ${deDateTime(seenByTeamAt)}`
                        : `Last viewed — ${deDateTime(seenByTeamAt)}`)}
                >
                  <Eye className="w-2.5 h-2.5" strokeWidth={2} />
                  <span>Team · {seenRel}</span>
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export {
  BookingHeaderAssigneeLines,
  BookingAssigneePicker,
  TeamReadOnlyCustomerChecklist,
  DE_LONG,
  WelcomeBackNoteEditor,
  TeamServicesChecklist,
  CancelConfirmation,
  BookingEditForm,
  ChecklistSection,
};
